import React, { useEffect, useRef, useState } from "react";
import { vorgangService, type VorgangPickerItem } from "../../services/vorgangService";
import type { RegelZielVorgang } from "./mailRegel";
import "./mailSettings.css";

/**
 * Schlanke, Fluent-freie Vorgangssuche für das Regel-Fenster (Cluster REGELN,
 * H4 — „Diesem Vorgang zuweisen").
 *
 * Bewusst eine eigene, schmale Komponente statt `MailVorgangPickerWindow`: das
 * ist ein eigenes Fenster mit Mehrfachauswahl und Fluent-Bausteinen — beides
 * wäre hier fehl am Platz, ein Fenster im Fenster erst recht.
 *
 * Als Datenquelle dient `GET /vorgang/picker` (Cluster VORGANG, D1–D3): sie
 * liefert bereits die gestufte Reihenfolge (Vorgänge des Kontakts → eigene/
 * Team → alle weiteren) und ein schlankes DTO mit Nummer, Kunde und Status.
 */

const DEBOUNCE_MS = 300;
const LIMIT = 25;

type Props = {
    /** Aktuell gewähltes Ziel (Radio-Verhalten: genau eines). */
    value: RegelZielVorgang | null;
    onChange: (ziel: RegelZielVorgang | null) => void;
    /** Rangfolge-Hinweis; steuert NIE die Sichtbarkeit. */
    contactId?: number | null;
};

export const MailRegelVorgangSuche: React.FC<Props> = ({ value, onChange, contactId }) => {
    const [term, setTerm] = useState("");
    const [treffer, setTreffer] = useState<VorgangPickerItem[]>([]);
    const [laedt, setLaedt] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);
    const abbruch = useRef<AbortController | null>(null);

    useEffect(() => {
        // Steht die Wahl schon fest, wird nicht weitergesucht — sonst blitzt die
        // Liste bei jedem Rerender wieder auf.
        if (value) return;
        const handle = window.setTimeout(async () => {
            abbruch.current?.abort();
            const controller = new AbortController();
            abbruch.current = controller;
            setLaedt(true);
            setFehler(null);
            try {
                const { items } = await vorgangService.getPickerPage({
                    q: term.trim() || undefined,
                    contactId: contactId ?? null,
                    limit: LIMIT,
                    offset: 0,
                    signal: controller.signal,
                });
                setTreffer(items);
            } catch (err) {
                if ((err as Error)?.name === "AbortError") return;
                setFehler("Vorgänge konnten nicht geladen werden.");
            } finally {
                setLaedt(false);
            }
        }, DEBOUNCE_MS);
        return () => window.clearTimeout(handle);
    }, [term, contactId, value]);

    useEffect(() => () => abbruch.current?.abort(), []);

    if (value) {
        return (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
                <span className="mset-badge">{value.label}</span>
                <button type="button" className="mset-btn" onClick={() => onChange(null)}>
                    Anderen Vorgang wählen
                </button>
            </div>
        );
    }

    return (
        <div style={{ marginTop: 6 }}>
            <input
                className="mset-inp"
                value={term}
                placeholder="Vorgang suchen — Betreff, Nummer oder Kunde"
                onChange={(e) => setTerm(e.target.value)}
                aria-label="Vorgang suchen"
            />
            {fehler ? (
                <div className="mset-hint" style={{ color: "var(--mset-err)" }}>
                    {fehler}
                </div>
            ) : laedt ? (
                <div className="mset-hint">Vorgänge werden geladen …</div>
            ) : treffer.length === 0 ? (
                <div className="mset-hint">Kein Vorgang gefunden.</div>
            ) : (
                <div className="mset-pick">
                    {treffer.map((t) => {
                        const v = t.vorgang;
                        const label = v.titel?.trim() || v.vorgangNbr || `Vorgang #${v.id}`;
                        return (
                            <button
                                key={v.id}
                                type="button"
                                className="mset-pick-row"
                                onClick={() => onChange({ id: v.id, label })}
                            >
                                <div style={{ fontWeight: 600 }}>{label}</div>
                                <div className="mset-pick-meta">
                                    {[v.vorgangNbr, v.kundeName, v.status].filter(Boolean).join(" · ")}
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default MailRegelVorgangSuche;
